import { ComponentPropsWithoutRef } from 'react';

import create from 'zustand';

import { IsEmptyObject } from '@utils/types';

import type { ModalMapType, ModalType } from './modals-map';

type ModalContainerOverride = {
  width?: string | number;
  autoWidth?: boolean;
  minWidth?: string | number;
  closeOnOverlayClickOrEsc?: boolean;
};

type ModalOverrideProps = {
  _container?: ModalContainerOverride;
};

type ModalVariant = 'standard' | 'fullscreen';
type ModalData<T extends ModalType = ModalType> = {
  modalType: ModalType;
  modalProps?: ModalComponentProps<T>;
  variant: ModalVariant;
};

type AfterCloseCallbacks = Partial<Record<ModalType, () => void>>;

type ModalStore = {
  data: ModalData | null;
  afterClose: AfterCloseCallbacks;
  show: typeof showModal;
  showFullscreen: typeof showModal;
  hide(): void;
  /** Register (or clear, with null) a one-shot callback to run when `modalType` closes. */
  setAfterClose(modalType: ModalType, callback: (() => void) | null): void;
};

// We supply our own closeModal but we don't want to include that in the `showModal(modal, props)` props.
type BaseModalProps<T extends ModalType> = Omit<
  ComponentPropsWithoutRef<ModalMapType[T]>,
  'closeModal'
>;
type EmptyModalProps<T extends ModalType> = IsEmptyObject<BaseModalProps<T>>;

type ComponentsWithProps = {
  [K in keyof ModalMapType]-?: EmptyModalProps<K> extends false ? K : never;
}[keyof ModalMapType];

type ComponentsWithoutProps = {
  [K in keyof ModalMapType]-?: EmptyModalProps<K> extends true ? K : never;
}[keyof ModalMapType];

type ModalComponentProps<T extends ModalType> = BaseModalProps<T> & ModalOverrideProps;

declare function showModal<T extends ComponentsWithProps>(
  name: T,
  props: ModalComponentProps<T>,
): void;
declare function showModal<T extends ComponentsWithoutProps>(
  name: T,
  props?: ModalOverrideProps,
): void;

export const useModalStore = create<ModalStore>((set, get) => ({
  data: null,
  afterClose: {},

  show: <T extends ModalType>(name: T, props?: ModalComponentProps<T> | ModalOverrideProps) => {
    const pending = get().afterClose;
    set({
      data: {
        modalProps: props,
        modalType: name,
        variant: 'standard',
      },
      afterClose: pending[name] ? { [name]: pending[name] } : {},
    });
  },

  showFullscreen: <T extends ModalType>(
    name: T,
    props?: ModalComponentProps<T> | ModalOverrideProps,
  ) => {
    const pending = get().afterClose;
    set({
      data: {
        modalProps: props,
        modalType: name,
        variant: 'fullscreen',
      },
      afterClose: pending[name] ? { [name]: pending[name] } : {},
    });
  },

  hide: () => {
    const { data, afterClose } = get();
    const callback = data ? afterClose[data.modalType] : undefined;
    set({ data: null, afterClose: {} });
    callback?.();
  },

  setAfterClose: (modalType, callback) =>
    set((state) => {
      const next = { ...state.afterClose };
      if (callback) {
        next[modalType] = callback;
      } else {
        delete next[modalType];
      }
      return { afterClose: next };
    }),
}));

export const useModal = () => {
  const showModal = useModalStore((state) => state.show);
  const showFullscreenModal = useModalStore((state) => state.showFullscreen);
  const hideModal = useModalStore((state) => state.hide);
  const setModalAfterClose = useModalStore((state) => state.setAfterClose);

  return {
    showModal,
    showFullscreenModal,
    hideModal,
    setModalAfterClose,
  };
};
